import { v4 as uuidv4 } from 'uuid';
import { supabaseHelpers, supabase } from './supabase';

class SupabaseApiService {
  constructor() {
    this.isSupabaseEnabled = !!(process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_ANON_KEY);
    // Supabase enabled status logged silently
  }

  // Auth methods
  async signup(email, password, parentName, familyName, captchaToken = null) {
    if (!this.isSupabaseEnabled) {
      throw new Error('Service is temporarily unavailable. Please try again later.');
    }

    try {
      // Creating real Supabase account
      const result = await supabaseHelpers.signUp(email, password, parentName, familyName, captchaToken);
      
      // Check if user needs email confirmation
      if (!result.user.email_confirmed_at) {
        // Email confirmation required
        return {
          needsConfirmation: true,
          email: email
        };
      }
      
      const authResult = {
        token: result.user.access_token,
        user: {
          id: result.user.id,
          email: result.user.email,
          parentName: parentName,
          familyName: familyName,
          role: 'parent', // Family owners are 'parent' type for presence tracking
          familyId: result.family.id
        },
        family: result.family
      };
      
      // Store in localStorage
      localStorage.setItem('authToken', authResult.token);
      localStorage.setItem('user', JSON.stringify(authResult.user));
      localStorage.setItem('familyData', JSON.stringify(result.family));
      
      return authResult;
    } catch (error) {
      console.error('Supabase signup error:', error);
      
      // If the error already has a user-friendly message (from our helper functions), use it
      if (error.message && (
          error.message.includes('email address is already registered') ||
          error.message.includes('email address has already been used') ||
          error.message.includes('An account already exists') ||
          error.message.includes('reserved for platform administration') ||
          error.message.includes('Please enter a valid email') ||
          error.message.includes('Password must be at least') ||
          error.message.includes('problem creating your family account')
        )) {
        throw error; // Pass through the user-friendly message
      }
      
      // For other errors, provide a generic fallback
      throw new Error(error.message || 'Failed to create account. Please try again.');
    }
  }

  async login(email, password, captchaToken = null) {
    if (!this.isSupabaseEnabled) {
      throw new Error('Service is temporarily unavailable. Please try again later.');
    }

    try {
      // Attempting real Supabase login
      const result = await supabaseHelpers.signIn(email, password, captchaToken);
      
      // Determine user type and create appropriate user object
      let userObject;
      if (result.isInvitedMember && result.memberProfile) {
        // This is an invited family member
        console.log('🎭 Formatting user object for invited member:', result.memberProfile.email);
        userObject = {
          id: result.user.id, // Supabase auth user ID
          email: result.user.email,
          parentName: `${result.memberProfile.first_name || ''} ${result.memberProfile.last_name || ''}`.trim(),
          familyName: result.family.family_name,
          role: 'parent', // All adult family members are 'parent' type for presence/calling
          familyId: result.family.id,
          memberId: result.memberProfile.id, // family_members table ID
          memberRole: result.memberProfile.role, // Their specific role (primary_parent, secondary_parent, etc.)
          isInvitedMember: true
        };
      } else {
        // This is the original parent/family creator
        console.log('👨‍💼 Formatting user object for original parent');
        userObject = {
          id: result.user.id,
          email: result.user.email,
          parentName: result.family.parent_name,
          familyName: result.family.family_name,
          role: 'parent',
          familyId: result.family.id,
          isInvitedMember: false
        };
      }
      
      const authResult = {
        token: result.user.access_token,
        user: userObject,
        family: this.formatFamilyData(result.family)
      };
      
      console.log('✅ Login successful! User object created:', {
        id: authResult.user.id,
        email: authResult.user.email,
        name: authResult.user.parentName,
        role: authResult.user.role,
        familyId: authResult.user.familyId,
        isInvitedMember: authResult.user.isInvitedMember
      });
      
      // Store in localStorage
      localStorage.setItem('authToken', authResult.token);
      localStorage.setItem('user', JSON.stringify(authResult.user));
      localStorage.setItem('familyData', JSON.stringify(authResult.family));
      
      return authResult;
    } catch (error) {
      console.error('Supabase login error:', error);
      throw new Error(error.message || 'Invalid credentials');
    }
  }

  async logout() {
    if (this.isSupabaseEnabled) {
      // Real Supabase logout
      // Supabase logout - clearing all data
      await supabaseHelpers.signOut();
    }
    
    // Always clear all localStorage data on logout
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('familyData');
  }

  // Rebuild app user + family state from an active Supabase session when
  // localStorage cache is missing or stale (page refresh / cleared cache).
  async restoreSessionFromAuth(sessionUser) {
    if (!this.isSupabaseEnabled) {
      throw new Error('Database not configured.');
    }
    if (!sessionUser?.id) {
      throw new Error('No active session user to restore.');
    }

    const family = await supabaseHelpers.getFamilyData(sessionUser.id);
    const formattedFamily = this.formatFamilyData(family);

    const { data: familyMembers } = await supabase
      .from('family_members')
      .select('*')
      .eq('user_id', sessionUser.id);

    const memberProfile = familyMembers?.[0] || null;
    const isInvitedMember = !!memberProfile && family.id !== sessionUser.id;

    let userObject;
    if (isInvitedMember) {
      userObject = {
        id: sessionUser.id,
        email: sessionUser.email,
        parentName: `${memberProfile.first_name || ''} ${memberProfile.last_name || ''}`.trim(),
        familyName: family.family_name,
        role: 'parent',
        familyId: family.id,
        memberId: memberProfile.id,
        memberRole: memberProfile.role,
        isInvitedMember: true
      };
    } else {
      userObject = {
        id: sessionUser.id,
        email: sessionUser.email,
        parentName: family.parent_name,
        familyName: family.family_name,
        role: 'parent',
        familyId: family.id,
        isInvitedMember: false
      };
    }

    localStorage.setItem('authToken', sessionUser.access_token || '');
    localStorage.setItem('user', JSON.stringify(userObject));
    localStorage.setItem('familyData', JSON.stringify(formattedFamily));

    return { user: userObject, family: formattedFamily };
  }

  // The browser holds only ONE Supabase auth session. If a second account logs
  // in (e.g. another tab), the cached "user" in localStorage no longer matches
  // the live session, and every read/write would silently target the OTHER
  // account's family. This guard makes that an explicit error instead.
  async getVerifiedUser() {
    const cachedUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (!cachedUser.id) {
      throw new Error('No user ID found. Please log in again.');
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      throw new Error('Your session has expired. Please log in again.');
    }

    if (session.user.id !== cachedUser.id) {
      throw new Error(
        `Account mismatch: this tab was using ${cachedUser.email}, but the browser is now signed in as ${session.user.email}. ` +
        'Only one account can be active per browser — please reload the page.'
      );
    }

    return cachedUser;
  }

  // Family data methods
  async getFamilyData() {
    if (!this.isSupabaseEnabled) {
      // Return cached local data
      const familyData = localStorage.getItem('familyData');
      return familyData ? JSON.parse(familyData) : null;
    }

    try {
      // Always ensure family exists first
      await this.ensureFamilyExists();
      
      const user = await this.getVerifiedUser();
      const family = await supabaseHelpers.getFamilyData(user.id);
      return this.formatFamilyData(family);
    } catch (error) {
      console.error('Failed to get family data:', error);
      throw error;
    }
  }

  // Add this method to fix family issues automatically
  async ensureFamilyExists() {
    if (!this.isSupabaseEnabled) return null;

    const user = await this.getVerifiedUser();

    try {
      // Use the helper function to get family data instead of direct supabase access
      const family = await supabaseHelpers.getFamilyData(user.id);
      return family;
    } catch (error) {
      if (error.message.includes('Family not found')) {
        // Family doesn't exist, create it using a new helper function
        console.log('Family missing, creating it automatically...');
        return await this.createFamilyRecord(user);
      }
      throw error;
    }
  }

  // Helper to create family record without direct supabase access
  async createFamilyRecord(user) {
    // We'll need to add this to supabaseHelpers instead of accessing supabase directly
    throw new Error('Please log out and sign up again to create your family account.');
  }

  // Update addKid with better family handling
  async addKid(kidData) {
    if (!this.isSupabaseEnabled) {
      throw new Error('Service is temporarily unavailable. Please try again later.');
    }
    
    try {
      const user = await this.getVerifiedUser();

      let familyId;
      try {
        // Try to get existing family
        const family = await supabaseHelpers.getFamilyData(user.id);
        familyId = family.id;
      } catch (error) {
        if (error.message.includes('Family not found')) {
          // Try to create the missing family record
          console.log('Creating missing family record...');
          try {
            const newFamily = await supabaseHelpers.createFamilyRecord(
              user.id, 
              user.email, 
              user.parentName, 
              user.familyName
            );
            familyId = newFamily.id;
            
            // Update localStorage with new family data
            const formattedFamily = this.formatFamilyData(newFamily);
            localStorage.setItem('familyData', JSON.stringify(formattedFamily));
            
            console.log('✅ Family record created successfully!');
          } catch (createError) {
            console.error('Failed to create family record:', createError);
            throw new Error('Could not create family account. Please log out and sign up again.');
          }
        } else {
          throw error;
        }
      }

      // Now add the kid
      const kid = await supabaseHelpers.addKid(familyId, kidData);
      console.log('✅ Kid added to database:', kid);
      
      // Add a small delay to ensure database consistency
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Retry fetching family data up to 3 times to ensure we get the new kid
      let updatedFamily;
      let retries = 3;
      
      while (retries > 0) {
        try {
          updatedFamily = await this.getFamilyData();
          
          // Check if the newly added kid is in the family data
          const newKidExists = updatedFamily.kids?.some(k => k.id === kid.id);
          
          if (newKidExists) {
            console.log('✅ New kid found in family data');
            break;
          } else {
            console.log(`⚠️ New kid not found, retrying... (${retries} attempts left)`);
            if (retries > 1) {
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          }
        } catch (fetchError) {
          console.error('Error fetching updated family data:', fetchError);
          if (retries === 1) throw fetchError;
        }
        
        retries--;
      }
      
      // If we still don't have the kid after retries, manually add it to the family data
      if (!updatedFamily.kids?.some(k => k.id === kid.id)) {
        console.log('⚠️ Manually adding kid to family data as fallback');
        const kidColors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#FF9FF3', '#54A0FF', '#5F27CD'];
        const formattedKid = {
          id: kid.id,
          name: kid.name,
          age: kid.age,
          avatar: kid.avatar,
          level: kid.level,
          points: kid.points,
          streak: kid.streak,
          profilePhoto: kid.profile_photo,
          faceDescriptor: kid.face_descriptor || null,
          color: kidColors[updatedFamily.kids?.length % kidColors.length],
          completedTasks: 0,
          tasks: [],
          achievements: [],
          stats: {
            bestStreakEver: 0,
            totalTasksCompleted: 0,
            averageCompletionTime: 15
          },
          preferences: {
            motivationalSounds: true,
            voiceCommands: true,
            allowRecording: true
          },
          parentConnection: {
            allowRecording: true,
            notifications: true
          }
        };
        
        updatedFamily.kids = [...(updatedFamily.kids || []), formattedKid];
      }
      
      console.log('📊 Final family data with new kid:', updatedFamily);
      
      return { family: updatedFamily, kid };
    } catch (error) {
      console.error('Failed to add kid:', error);
      throw new Error('Failed to add kid: ' + error.message);
    }
  }

  async updateKid(kidId, updates) {
    if (!this.isSupabaseEnabled) {
      throw new Error('Service is temporarily unavailable. Please try again later.');
    }

    try {
      await supabaseHelpers.updateKid(kidId, updates);
      return await this.getFamilyData();
    } catch (error) {
      console.error('Failed to update kid:', error);
      throw error;
    }
  }

  /**
   * Set or clear the 4-digit parent PIN used to skip face auth.
   * Stored in families.settings.parent_pin (merged with existing settings).
   */
  async setParentPin(pin) {
    if (!this.isSupabaseEnabled) {
      throw new Error('Service is temporarily unavailable. Please try again later.')
    }

    const normalized = pin == null || pin === '' ? null : String(pin).trim()
    if (normalized !== null && !/^\d{4}$/.test(normalized)) {
      throw new Error('Parent PIN must be exactly 4 digits')
    }

    const user = await this.getVerifiedUser()
    const rawFamily = await supabaseHelpers.getFamilyData(user.id)
    const nextSettings = {
      ...(rawFamily.settings || {}),
      parent_pin: normalized
    }
    await supabaseHelpers.updateFamilySettings(rawFamily.id, nextSettings)
    return await this.getFamilyData()
  }

  async removeKid(kidId) {
    if (!this.isSupabaseEnabled) {
      throw new Error('Service is temporarily unavailable. Please try again later.');
    }

    try {
      await supabaseHelpers.deleteKid(kidId);
      return await this.getFamilyData();
    } catch (error) {
      console.error('Failed to remove kid:', error);
      throw error;
    }
  }

  // Task management
  async completeTask(kidId, taskId, completionData = {}) {
    if (!this.isSupabaseEnabled) {
      throw new Error('Service is temporarily unavailable. Please try again later.');
    }

    try {
      await supabaseHelpers.completeTask(kidId, taskId, completionData);
      const updatedFamily = await this.getFamilyData();
      
      const kid = updatedFamily.kids?.find(k => k.id === kidId);
      const task = kid?.tasks?.find(t => t.id === taskId);
      
      return { family: updatedFamily, kid, task };
    } catch (error) {
      console.error('Failed to complete task:', error);
      throw error;
    }
  }

  async addTask(kidId, taskData) {
    if (!this.isSupabaseEnabled) {
      throw new Error('Service is temporarily unavailable. Please try again later.');
    }

    try {
      const result = await supabaseHelpers.addTask(kidId, taskData);
      const updatedFamily = await this.getFamilyData();
      return { family: updatedFamily, task: result };
    } catch (error) {
      console.error('Failed to add task:', error);
      throw error;
    }
  }

  // Task management methods
  async addTaskToKid(kidId, taskData) {
    if (!this.isSupabaseEnabled) {
      throw new Error('Service is temporarily unavailable. Please try again later.');
    }

    try {
      console.log('🔄 Adding task to kid:', kidId, 'Task data:', taskData);
      
      // Use the proper addTask method that inserts into the tasks table
      const newTask = await supabaseHelpers.addTask(kidId, taskData);
      console.log('✅ Task added to database:', newTask);
      
      // Get updated family data that includes the new task
      const refreshedFamily = await this.getFamilyData();
      console.log('📊 Refreshed family data:', refreshedFamily);
      
      // Update localStorage cache immediately
      localStorage.setItem('familyData', JSON.stringify(refreshedFamily));
      
      return { task: newTask, family: refreshedFamily };
    } catch (error) {
      console.error('❌ Failed to add task:', error);
      throw error;
    }
  }

  async removeTaskFromKid(kidId, taskId) {
    if (!this.isSupabaseEnabled) {
      throw new Error('Service is temporarily unavailable. Please try again later.');
    }

    try {
      console.log('🔄 Removing task:', taskId, 'from kid:', kidId);
      
      // Delete the task from the tasks table
      await supabaseHelpers.removeTask(taskId);
      console.log('✅ Task removed from database');
      
      // Get updated family data
      const refreshedFamily = await this.getFamilyData();
      console.log('📊 Refreshed family data after task removal');
      
      return { family: refreshedFamily };
    } catch (error) {
      console.error('❌ Failed to remove task:', error);
      throw error;
    }
  }

  async completeTaskForKid(kidId, taskId) {
    if (!this.isSupabaseEnabled) {
      throw new Error('Service is temporarily unavailable. Please try again later.');
    }

    try {
      console.log('🔄 Completing task:', taskId, 'for kid:', kidId);
      
      // Mark the task as completed in the tasks table
      const completedTask = await supabaseHelpers.updateTask(taskId, {
        completed: true,
        completed_at: new Date().toISOString()
      });
      console.log('✅ Task marked as completed:', completedTask);
      
      // Update the kid's points and level
      const family = await this.getFamilyData();
      const kid = family.kids.find(k => k.id === kidId);
      
      if (!kid) {
        throw new Error('Kid not found');
      }

      const newPoints = kid.points + completedTask.points;
      const newCompletedTasks = (kid.completed_tasks || 0) + 1;
      const newLevel = Math.floor(newPoints / 100) + 1;
      
      // Check for new achievements
      const newAchievements = this.checkForAchievements(kid, {
        newPoints,
        newCompletedTasks,
        newLevel,
        taskJustCompleted: true
      });
      
      // Update kid's stats in the database
      await supabaseHelpers.updateKid(kidId, {
        points: newPoints,
        level: newLevel
        // Note: completed_tasks is now calculated from the tasks table
      });
      
      console.log('✅ Kid stats updated');
      
      // Get fresh family data
      const updatedFamily = await this.getFamilyData();
      
      return { 
        task: completedTask, 
        kid: updatedFamily.kids.find(k => k.id === kidId), 
        family: updatedFamily,
        newAchievements 
      };
    } catch (error) {
      console.error('❌ Failed to complete task:', error);
      throw error;
    }
  }

  // Shopping list methods
  async addShoppingItem(itemData) {
    if (!this.isSupabaseEnabled) {
      throw new Error('Service is temporarily unavailable. Please try again later.');
    }

    try {
      const user = await this.getVerifiedUser();

      // Try to get family first
      let familyId;
      try {
        const family = await supabaseHelpers.getFamilyData(user.id);
        familyId = family.id;
      } catch (error) {
        if (error.message.includes('Family not found')) {
          throw new Error('Family account not properly set up. Please log out and sign up again to fix this issue.');
        }
        throw error;
      }

      const item = await supabaseHelpers.addShoppingItem(familyId, itemData);
      const updatedFamily = await this.getFamilyData();
      
      return { family: updatedFamily, item };
    } catch (error) {
      console.error('Failed to add shopping item:', error);
      throw new Error('Failed to add shopping item: ' + error.message);
    }
  }

  async removeShoppingItem(itemId) {
    if (!this.isSupabaseEnabled) {
      throw new Error('Service is temporarily unavailable. Please try again later.');
    }

    try {
      await supabaseHelpers.deleteShoppingItem(itemId);
      const updatedFamily = await this.getFamilyData();
      
      return { family: updatedFamily };
    } catch (error) {
      console.error('Failed to remove shopping item:', error);
      throw error;
    }
  }

  async toggleShoppingItem(itemId) {
    if (!this.isSupabaseEnabled) {
      throw new Error('Service is temporarily unavailable. Please try again later.');
    }

    try {
      await supabaseHelpers.toggleShoppingItem(itemId);
      const updatedFamily = await this.getFamilyData();
      
      return { family: updatedFamily };
    } catch (error) {
      console.error('Failed to toggle shopping item:', error);
      throw error;
    }
  }

  // Helper methods

  formatFamilyData(rawFamily) {
    // Convert Supabase production data format to app format
    const kidColors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#FF9FF3', '#54A0FF', '#5F27CD'];
    
    return {
      // Basic family info
      id: rawFamily.id,
      familyName: rawFamily.family_name,
      parentName: rawFamily.parent_name,
      parentEmail: rawFamily.parent_email,
      
      // Subscription system - BOTH nested and top-level for compatibility
      subscription_tier: rawFamily.subscription_tier || 'free',
      subscription_status: rawFamily.subscription_status || 'active',
      has_active_override: rawFamily.has_active_override || false,
      original_tier: rawFamily.original_tier,
      override_expires_at: rawFamily.override_expires_at,
      
      subscription: {
        tier: rawFamily.subscription_tier || 'free',
        status: rawFamily.subscription_status || 'active',
        stripeCustomerId: rawFamily.stripe_customer_id
      },
      
      // Advanced family settings (JSONB)
      settings: {
        currency: rawFamily.settings?.currency || 'USD',
        language: rawFamily.settings?.language || 'en',
        timezone: rawFamily.settings?.timezone || 'UTC',
        dailyPointsLimit: rawFamily.settings?.daily_points_limit || 1000,
        maxKidsWithLogin: rawFamily.settings?.max_kids_with_login || 5,
        notificationsEnabled: rawFamily.settings?.notifications_enabled ?? true,
        taskApprovalRequired: rawFamily.settings?.task_approval_required ?? false,
        requireParentApproval: rawFamily.settings?.require_parent_approval ?? true,
            islamicFeaturesEnabled: rawFamily.settings?.islamic_features_enabled ?? false,
            kidRegistrationEnabled: rawFamily.settings?.kid_registration_enabled ?? true,
            parentPin: rawFamily.settings?.parent_pin || null
          },
      
      // Islamic settings (JSONB)
      islamicSettings: {
        enabled: rawFamily.islamic_settings?.enabled ?? false,
        location: rawFamily.islamic_settings?.location || { lat: null, lng: null, city: null },
        adhanSound: rawFamily.islamic_settings?.adhan_sound ?? true,
        fridayReminder: rawFamily.islamic_settings?.friday_reminder ?? true,
        calculationMethod: rawFamily.islamic_settings?.calculation_method || 'MWL',
        prayerNotifications: rawFamily.islamic_settings?.prayer_notifications ?? true
      },
      
      // Advanced kids with all features
      kids: rawFamily.kids?.map((kid, index) => ({
        // Basic info
        id: kid.id,
        name: kid.name,
        age: kid.age,
        avatar: kid.avatar,
        profilePhoto: kid.profile_photo,
        faceDescriptor: kid.face_descriptor || null,
        color: kidColors[index % kidColors.length],
        
        // Authentication system
        email: kid.email,
        userId: kid.user_id,
        canLogin: kid.can_login || false,
        lastLogin: kid.last_login,
        loginAttempts: kid.login_attempts || 0,
        accountLocked: kid.account_locked || false,
        lockedUntil: kid.locked_until,
        
        // Advanced points system
        level: kid.level || 1,
        points: kid.points || 0,
        totalPoints: kid.total_points || 0,
        streak: kid.streak || 0,
        bestStreak: kid.best_streak || 0,
        
        // Rich JSONB settings
        settings: {
          theme: kid.settings?.theme || 'auto',
          dailyGoal: kid.settings?.daily_goal || 100,
          privacyMode: kid.settings?.privacy_mode ?? false,
          notifications: kid.settings?.notifications ?? true,
          soundEffects: kid.settings?.sound_effects ?? true,
          preferredCategories: kid.settings?.preferred_categories || [],
          // Legacy mapping for existing components
          motivationalSounds: kid.settings?.sound_effects ?? true,
          voiceCommands: kid.settings?.notifications ?? true,
          allowRecording: kid.settings?.notifications ?? true
        },
        
        // Rich JSONB stats
        stats: {
          daysActive: kid.stats?.days_active || 0,
          weeklyPoints: kid.stats?.weekly_points || 0,
          monthlyPoints: kid.stats?.monthly_points || 0,
          tasksCompleted: kid.stats?.tasks_completed || 0,
          achievementsEarned: kid.stats?.achievements_earned || 0,
          favoriteCategories: kid.stats?.favorite_categories || [],
          // Legacy mapping for existing components
          bestStreakEver: kid.best_streak || kid.streak || 0,
          totalTasksCompleted: kid.stats?.tasks_completed || kid.completed_tasks || 0,
          averageCompletionTime: 15
        },
        
        // Islamic profile (JSONB)
        islamicProfile: {
          quranReading: kid.islamic_profile?.quran_reading ?? false,
          prayerTracking: kid.islamic_profile?.prayer_tracking ?? false,
          islamicAchievements: kid.islamic_profile?.islamic_achievements || []
        },
        
        // Legacy fields for compatibility
        completedTasks: kid.completed_tasks || 0,
        tasks: kid.tasks || [],
        completedTasksHistory: kid.completedTasksHistory || [],
        achievements: kid.achievements || [],
        preferences: {
          motivationalSounds: kid.settings?.sound_effects ?? true,
          voiceCommands: kid.settings?.notifications ?? true,
          allowRecording: kid.settings?.notifications ?? true
        },
        parentConnection: {
          allowRecording: kid.settings?.notifications ?? true,
          notifications: kid.settings?.notifications ?? true
        }
      })) || [],
      
      // Advanced shopping list with e-commerce features
      shoppingList: rawFamily.shopping_items?.map(item => ({
        id: item.id,
        name: item.item_name || item.name || 'Unnamed Item', // Use correct column name
        description: item.description,
        category: item.category,
        quantity: item.quantity || 1,
        estimatedPrice: item.estimated_price,
        urgent: item.urgent || false,
        purchased: item.purchased || false,
        purchasedAt: item.purchased_at,
        purchasedBy: item.purchased_by,
        addedBy: item.added_by_kid_id || item.added_by_user_id || 'Parent',
        addedByKidId: item.added_by_kid_id,
        addedByUserId: item.added_by_user_id,
        notes: item.notes,
        createdAt: item.created_at,
        updatedAt: item.updated_at
      })) || [],
      
      // Other advanced features
      familyChallenges: rawFamily.family_challenges || [],
      messages: rawFamily.messages || [],
      
      // Metadata
      createdAt: rawFamily.created_at,
      updatedAt: rawFamily.updated_at
    };
  }

  // Offline synchronization methods
  queueOfflineChange(changeData) {
    if (!this.isSupabaseEnabled) {
      console.log('⚠️ Supabase not enabled, skipping offline queue');
      return;
    }
    
    const offlineQueue = JSON.parse(localStorage.getItem('offlineQueue') || '[]');
    offlineQueue.push({
      ...changeData,
      timestamp: Date.now(),
      id: `offline-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    });
    localStorage.setItem('offlineQueue', JSON.stringify(offlineQueue));
    console.log('📱 Queued offline change:', changeData);
  }

  async syncWhenOnline() {
    if (!this.isSupabaseEnabled) {
      console.log('⚠️ Supabase not enabled, skipping sync');
      return;
    }
    
    if (!navigator.onLine) {
      console.log('📱 Still offline, skipping sync');
      return;
    }
    
    const offlineQueue = JSON.parse(localStorage.getItem('offlineQueue') || '[]');
    if (offlineQueue.length === 0) {
      console.log('✅ No offline changes to sync');
      return;
    }
    
    console.log(`🔄 Syncing ${offlineQueue.length} offline changes...`);
    
    const syncPromises = offlineQueue.map(async (change) => {
      try {
        switch (change.type) {
          case 'completeTask':
            await this.completeTask(change.kidId, change.taskId, change.data);
            break;
          case 'updateKid':
            await this.updateKid(change.kidId, change.updates);
            break;
          case 'addShoppingItem':
            await this.addShoppingItem(change.data);
            break;
          case 'removeShoppingItem':
            await this.removeShoppingItem(change.itemId);
            break;
          case 'toggleShoppingItem':
            await this.toggleShoppingItem(change.itemId);
            break;
          default:
            console.warn('Unknown offline change type:', change.type);
        }
        return { success: true, change };
      } catch (error) {
        console.error('Failed to sync offline change:', change, error);
        return { success: false, change, error };
      }
    });
    
    const results = await Promise.allSettled(syncPromises);
    const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
    const failed = results.filter(r => r.status === 'rejected' || !r.value.success).length;
    
    if (failed === 0) {
      // All synced successfully, clear the queue
      localStorage.removeItem('offlineQueue');
      console.log(`✅ Successfully synced all ${successful} offline changes`);
    } else {
      // Keep failed changes in queue
      const failedChanges = results
        .filter(r => r.status === 'rejected' || !r.value.success)
        .map(r => r.value?.change || r.reason);
      localStorage.setItem('offlineQueue', JSON.stringify(failedChanges));
      console.log(`⚠️ Synced ${successful} changes, ${failed} failed and will retry later`);
    }
  }

  // Utility methods
  isAuthenticated() {
    return !!localStorage.getItem('authToken');
  }

  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  getCachedFamilyData() {
    const familyStr = localStorage.getItem('familyData');
    return familyStr ? JSON.parse(familyStr) : null;
  }

  // Add this new method to check for achievements
  checkForAchievements(kid, stats) {
    const { newPoints, newCompletedTasks, newLevel, taskJustCompleted } = stats;
    const currentAchievements = kid.achievements || [];
    const newAchievements = [];
    
    // Helper to check if achievement already earned
    const hasAchievement = (name) => currentAchievements.some(a => a.name === name);
    
    // First Task Master - complete first task
    if (newCompletedTasks === 1 && taskJustCompleted && !hasAchievement('First Task Master')) {
      newAchievements.push({
        id: uuidv4(),
        name: 'First Task Master',
        description: 'Completed your very first task!',
        icon: '🏆',
        unlockedAt: new Date().toISOString(),
        points: 50
      });
    }
    
    // Level Up! - reach level 2
    if (newLevel >= 2 && !hasAchievement('Level Up!')) {
      newAchievements.push({
        id: uuidv4(),
        name: 'Level Up!',
        description: 'Reached level 2!',
        icon: '⬆️',
        unlockedAt: new Date().toISOString(),
        points: 100
      });
    }
    
    // Century Club - earn 100 points
    if (newPoints >= 100 && !hasAchievement('Century Club')) {
      newAchievements.push({
        id: uuidv4(),
        name: 'Century Club',
        description: 'Earned 100 points!',
        icon: '💯',
        unlockedAt: new Date().toISOString(),
        points: 200
      });
    }
    
    // Point Powerhouse - earn 500 points
    if (newPoints >= 500 && !hasAchievement('Point Powerhouse')) {
      newAchievements.push({
        id: uuidv4(),
        name: 'Point Powerhouse',
        description: 'Earned 500 points!',
        icon: '⚡',
        unlockedAt: new Date().toISOString(),
        points: 500
      });
    }
    
    // Task achievements based on total completed
    if (newCompletedTasks >= 10 && !hasAchievement('Weekly Warrior')) {
      newAchievements.push({
        id: uuidv4(),
        name: 'Weekly Warrior',
        description: 'Completed 10 tasks!',
        icon: '🗡️',
        unlockedAt: new Date().toISOString(),
        points: 150
      });
    }
    
    if (newCompletedTasks >= 30 && !hasAchievement('Monthly Champion')) {
      newAchievements.push({
        id: uuidv4(),
        name: 'Monthly Champion',
        description: 'Completed 30 tasks!',
        icon: '👑',
        unlockedAt: new Date().toISOString(),
        points: 300
      });
    }
    
    return newAchievements;
  }

  // ========================================================
  // CALLING MANAGEMENT METHODS
  // ========================================================

  async updateCallingSettings(callingEnabled, callingSettings = {}) {
    if (!this.isSupabaseEnabled) {
      throw new Error('Database not configured');
    }

    try {
      const result = await supabaseHelpers.updateCallingSettings(callingEnabled, callingSettings);
      return result;
    } catch (error) {
      console.error('Update calling settings error:', error);
      throw new Error(error.message || 'Failed to update calling settings');
    }
  }

  async getCallingSettings() {
    if (!this.isSupabaseEnabled) {
      throw new Error('Database not configured');
    }

    try {
      const result = await supabaseHelpers.getCallingSettings();
      return result;
    } catch (error) {
      console.error('Get calling settings error:', error);
      throw new Error(error.message || 'Failed to get calling settings');
    }
  }

  async updateKidCallingPermissions(kidId, permissions) {
    if (!this.isSupabaseEnabled) {
      throw new Error('Database not configured');
    }

    try {
      const result = await supabaseHelpers.updateKidCallingPermissions(kidId, permissions);
      return result;
    } catch (error) {
      console.error('Update kid calling permissions error:', error);
      throw new Error(error.message || 'Failed to update calling permissions');
    }
  }

  async checkFamilyCallingFeatures() {
    if (!this.isSupabaseEnabled) {
      return { hasCallingFeatures: false, reason: 'Database not configured' };
    }

    try {
      const result = await supabaseHelpers.checkFamilyCallingFeatures();
      return result;
    } catch (error) {
      console.error('Check calling features error:', error);
      return { hasCallingFeatures: false, reason: 'Error checking features' };
    }
  }

  // ========================================================
  // PRESENCE SYSTEM METHODS
  // ========================================================

  async updatePresence(userType, userId, userName = '', isOnline = true, isAvailableForCalls = true, statusMessage = '', currentActivity = 'active', deviceInfo = {}) {
    if (!this.isSupabaseEnabled) {
      return { success: false, reason: 'Database not configured' };
    }

    try {
      const result = await supabaseHelpers.updatePresence(userType, userId, userName, isOnline, isAvailableForCalls, statusMessage, currentActivity, deviceInfo);
      return { success: true, data: result };
    } catch (error) {
      console.error('Update presence error:', error);
      return { success: false, error: error.message };
    }
  }

  async markUserOffline(userId) {
    if (!this.isSupabaseEnabled) {
      return { success: false, reason: 'Database not configured' };
    }

    try {
      const result = await supabaseHelpers.markUserOffline(userId);
      return { success: true, data: result };
    } catch (error) {
      console.error('Mark user offline error:', error);
      return { success: false, error: error.message };
    }
  }

  async getFamilyPresence(familyId = null) {
    if (!this.isSupabaseEnabled) {
      return [];
    }

    try {
      const result = await supabaseHelpers.getFamilyPresence(familyId);
      return result;
    } catch (error) {
      console.error('Get family presence error:', error);
      return [];
    }
  }

  subscribeToPresenceChanges(familyId, callback) {
    if (!this.isSupabaseEnabled) {
      return null;
    }

    try {
      return supabaseHelpers.subscribeToPresenceChanges(familyId, callback);
    } catch (error) {
      console.error('Subscribe to presence changes error:', error);
      return null;
    }
  }

  async getKidCallSettings(kidId) {
    if (!this.isSupabaseEnabled) {
      return null;
    }

    try {
      const result = await supabaseHelpers.getKidCallSettings(kidId);
      return result;
    } catch (error) {
      console.error('Get kid call settings error:', error);
      return null;
    }
  }

  // ========================================================
  // CALLING & SIGNALING METHODS
  // ========================================================

  async initiateCall(callData) {
    if (!this.isSupabaseEnabled) {
      return { success: false, reason: 'Database not configured' };
    }

    try {
      const result = await supabaseHelpers.initiateCall(callData);
      return { success: true, data: result };
    } catch (error) {
      console.error('Initiate call error:', error);
      return { success: false, error: error.message };
    }
  }

  async answerCall(callId) {
    if (!this.isSupabaseEnabled) {
      return { success: false, reason: 'Database not configured' };
    }

    try {
      const result = await supabaseHelpers.answerCall(callId);
      return { success: true, data: result };
    } catch (error) {
      console.error('Answer call error:', error);
      return { success: false, error: error.message };
    }
  }

  async rejectCall(callId) {
    if (!this.isSupabaseEnabled) {
      return { success: false, reason: 'Database not configured' };
    }

    try {
      const result = await supabaseHelpers.rejectCall(callId);
      return { success: true, data: result };
    } catch (error) {
      console.error('Reject call error:', error);
      return { success: false, error: error.message };
    }
  }

  async endCall(callId) {
    if (!this.isSupabaseEnabled) {
      return { success: false, reason: 'Database not configured' };
    }

    try {
      const result = await supabaseHelpers.endCall(callId);
      return { success: true, data: result };
    } catch (error) {
      console.error('End call error:', error);
      return { success: false, error: error.message };
    }
  }

  async sendCallSignal(signalData) {
    if (!this.isSupabaseEnabled) {
      return { success: false, reason: 'Database not configured' };
    }

    try {
      const result = await supabaseHelpers.sendCallSignal(signalData);
      return { success: true, data: result };
    } catch (error) {
      console.error('Send call signal error:', error);
      return { success: false, error: error.message };
    }
  }

  // Synchronous, unload-safe teardown (keepalive fetch). Not async on purpose.
  endCallBeacon(callId, accessToken) {
    if (!this.isSupabaseEnabled) return false;
    try {
      return supabaseHelpers.endCallBeacon(callId, accessToken);
    } catch (error) {
      console.error('End call beacon error:', error);
      return false;
    }
  }

  async getAccessToken() {
    if (!this.isSupabaseEnabled) return null;
    try {
      return await supabaseHelpers.getAccessToken();
    } catch (error) {
      return null;
    }
  }

  async subscribeToCallSignaling(callId, callback) {
    if (!this.isSupabaseEnabled) {
      return null;
    }

    try {
      return supabaseHelpers.subscribeToCallSignaling(callId, callback);
    } catch (error) {
      console.error('Subscribe to call signaling error:', error);
      return null;
    }
  }

  async getCallHistory(limit = 50) {
    if (!this.isSupabaseEnabled) {
      return [];
    }

    try {
      const result = await supabaseHelpers.getCallHistory(limit);
      return result;
    } catch (error) {
      console.error('Get call history error:', error);
      return [];
    }
  }

  async subscribeToIncomingCalls(callback) {
    if (!this.isSupabaseEnabled) {
      return null;
    }

    try {
      return await supabaseHelpers.subscribeToIncomingCalls(callback);
    } catch (error) {
      console.error('Subscribe to incoming calls error:', error);
      return null;
    }
  }

  async subscribeToCallLifecycle(callback) {
    if (!this.isSupabaseEnabled) {
      return null;
    }

    try {
      return await supabaseHelpers.subscribeToCallLifecycle(callback);
    } catch (error) {
      console.error('Subscribe to call lifecycle error:', error);
      return null;
    }
  }

  async getPendingIncomingCalls() {
    if (!this.isSupabaseEnabled) {
      return [];
    }

    try {
      return await supabaseHelpers.getPendingIncomingCalls();
    } catch (error) {
      console.error('Get pending incoming calls error:', error);
      return [];
    }
  }

  async getCallSettings(memberId) {
    if (!this.isSupabaseEnabled) {
      return { success: false, reason: 'Database not configured' };
    }

    try {
      const result = await supabaseHelpers.getCallSettings(memberId);
      return result;
    } catch (error) {
      console.error('Get call settings error:', error);
      return null;
    }
  }

  async updateCallSettings(memberId, settings) {
    if (!this.isSupabaseEnabled) {
      return { success: false, reason: 'Database not configured' };
    }

    try {
      const result = await supabaseHelpers.updateCallSettings(memberId, settings);
      return { success: true, data: result };
    } catch (error) {
      console.error('Update call settings error:', error);
      return { success: false, error: error.message };
    }
  }

  // ========================================================
  // ADMIN DASHBOARD FUNCTIONS
  // ========================================================

  // Get all families for admin dashboard
  async getAllFamilies() {
    try {
      const { data, error } = await supabase
        .from('families')
        .select(`
          id,
          family_name,
          parent_name,
          parent_email,
          subscription_tier,
          subscription_status,
          stripe_customer_id,
          stripe_subscription_id,
          created_at,
          updated_at,
          calling_enabled,
          monthly_call_minutes_used,
          monthly_call_minutes_limit
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching all families:', error);
      return [];
    }
  }

  /**
   * Recent family signups for admin integrity monitoring.
   * Uses families table (visible to admins via is_admin RLS).
   */
  async getRecentSignups({ days = 30, limit = 50 } = {}) {
    try {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);

      const { data, error } = await supabase
        .from('families')
        .select(`
          id,
          family_name,
          parent_name,
          parent_email,
          subscription_tier,
          subscription_status,
          created_at
        `)
        .gte('created_at', cutoff.toISOString())
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      const now = Date.now();
      const dayMs = 24 * 60 * 60 * 1000;
      const rows = data || [];

      return {
        signups: rows,
        counts: {
          last24h: rows.filter((r) => now - new Date(r.created_at).getTime() < dayMs).length,
          last7d: rows.filter((r) => now - new Date(r.created_at).getTime() < 7 * dayMs).length,
          last30d: rows.length,
        },
      };
    } catch (error) {
      console.error('Error fetching recent signups:', error);
      return { signups: [], counts: { last24h: 0, last7d: 0, last30d: 0 } };
    }
  }

  async getPendingInvitations({ limit = 50 } = {}) {
    try {
      const { data, error } = await supabase
        .from('family_members')
        .select(`
          id,
          family_id,
          email,
          first_name,
          last_name,
          role,
          invitation_status,
          invitation_expires_at,
          created_at,
          families:family_id (
            family_name,
            parent_email
          )
        `)
        .eq('invitation_status', 'pending')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching pending invitations:', error);
      return [];
    }
  }

  // Debug function to check specific family subscription status
  async debugFamilySubscription(email) {
    try {
      console.log('🔍 DEBUG: Checking family subscription for email:', email);
      
      // Get family data
      const { data: family, error: familyError } = await supabase
        .from('families')
        .select(`
          id,
          family_name,
          parent_name,
          parent_email,
          subscription_tier,
          subscription_status,
          stripe_customer_id,
          stripe_subscription_id,
          created_at,
          updated_at,
          has_active_override,
          original_tier,
          override_expires_at
        `)
        .eq('parent_email', email)
        .single();

      if (familyError) {
        console.error('❌ Error fetching family:', familyError);
        return { error: familyError.message };
      }

      // Get active overrides
      const { data: overrides, error: overrideError } = await supabase
        .from('subscription_overrides')
        .select('*')
        .eq('family_id', family.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (overrideError) {
        console.error('❌ Error fetching overrides:', overrideError);
      }

      const result = {
        family: {
          id: family.id,
          name: family.family_name,
          email: family.parent_email,
          subscription_tier: family.subscription_tier,
          subscription_status: family.subscription_status,
          has_active_override: family.has_active_override,
          original_tier: family.original_tier,
          override_expires_at: family.override_expires_at
        },
        activeOverrides: overrides || [],
        debugInfo: {
          queryTime: new Date().toISOString(),
          familyFound: !!family,
          overridesFound: overrides?.length || 0
        }
      };

      console.log('📊 DEBUG RESULT:', result);
      return result;
    } catch (error) {
      console.error('❌ Debug error:', error);
      return { error: error.message };
    }
  }

  // Get all subscriptions for admin dashboard
  async getAllSubscriptions() {
    try {
      const { data, error } = await supabase
        .from('families')
        .select(`
          id,
          family_name,
          subscription_tier,
          subscription_status,
          stripe_subscription_id,
          subscription_current_period_start,
          subscription_current_period_end
        `)
        .not('subscription_tier', 'eq', 'free')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Transform data to match subscription format
      return data.map(family => ({
        id: family.id,
        family_name: family.family_name,
        plan_name: family.subscription_tier?.toUpperCase() || 'FREE',
        amount: this.getPlanPrice(family.subscription_tier),
        status: family.subscription_status || 'active',
        next_billing_date: family.subscription_current_period_end
      }));
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      return [];
    }
  }

  // Get revenue metrics for admin dashboard
  async getRevenueMetrics() {
    try {
      // Get all families with subscriptions
      const { data: families, error } = await supabase
        .from('families')
        .select('subscription_tier, subscription_status, created_at')
        .not('subscription_tier', 'eq', 'free');

      if (error) throw error;

      // Calculate metrics
      const activeSubscriptions = families.filter(f => f.subscription_status === 'active');
      const monthlyRevenue = activeSubscriptions.reduce((total, family) => {
        return total + this.getPlanPrice(family.subscription_tier);
      }, 0);

      const totalRevenue = monthlyRevenue * 12; // Annual projection
      const avgRevenuePerFamily = activeSubscriptions.length > 0 ? monthlyRevenue / activeSubscriptions.length : 0;
      
      // Calculate churn rate (simplified)
      const canceledSubscriptions = families.filter(f => f.subscription_status === 'canceled').length;
      const churnRate = families.length > 0 ? (canceledSubscriptions / families.length) * 100 : 0;

      return {
        monthlyRevenue,
        totalRevenue,
        avgRevenuePerFamily,
        churnRate,
        activeSubscriptions: activeSubscriptions.length,
        totalSubscriptions: families.length
      };
    } catch (error) {
      console.error('Error calculating revenue metrics:', error);
      return {
        monthlyRevenue: 0,
        totalRevenue: 0,
        avgRevenuePerFamily: 0,
        churnRate: 0,
        activeSubscriptions: 0,
        totalSubscriptions: 0
      };
    }
  }

  // Helper function to get plan prices
  getPlanPrice(tier) {
    const prices = {
      'premium': 19.99,
      'family_plus': 39.99,
      'free': 0
    };
    return prices[tier] || 0;
  }

  // Suspend a family account
  async suspendFamily(familyId) {
    try {
      const { error } = await supabase
        .from('families')
        .update({ subscription_status: 'canceled' })
        .eq('id', familyId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error suspending family:', error);
      return { success: false, error: error.message };
    }
  }

  // Activate a family account
  async activateFamily(familyId) {
    try {
      const { error } = await supabase
        .from('families')
        .update({ subscription_status: 'active' })
        .eq('id', familyId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error activating family:', error);
      return { success: false, error: error.message };
    }
  }

  // Update family subscription
  async updateFamilySubscription(familyId, subscriptionData) {
    try {
      const { error } = await supabase
        .from('families')
        .update(subscriptionData)
        .eq('id', familyId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error updating family subscription:', error);
      return { success: false, error: error.message };
    }
  }

  // Delete a family (admin only)
  async deleteFamily(familyId) {
    try {
      const { error } = await supabase
        .from('families')
        .delete()
        .eq('id', familyId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting family:', error);
      return { success: false, error: error.message };
    }
  }

  // ========================================================
  // MANUAL SUBSCRIPTION OVERRIDE SYSTEM
  // ========================================================

  // Apply subscription override
  async applySubscriptionOverride(familyId, overrideData) {
    try {
      const { data, error } = await supabase.rpc('apply_subscription_override', {
        p_family_id: familyId,
        p_override_tier: overrideData.override_tier,
        p_override_reason: overrideData.override_reason,
        p_reason_notes: overrideData.reason_notes || '',
        p_expires_at: overrideData.expires_at,
        p_created_by_admin: overrideData.created_by_admin,
        p_billing_paused: overrideData.billing_paused !== false
      });

      if (error) throw error;

      // Force refresh family data cache for this family to immediately reflect changes
      await this.refreshFamilyDataForSubscriptionChange(familyId);
      
      return { success: true, overrideId: data };
    } catch (error) {
      console.error('Error applying subscription override:', error);
      return { success: false, error: error.message };
    }
  }

  // Revert subscription override
  async revertSubscriptionOverride(familyId, revertNotes = null) {
    try {
      const { data, error } = await supabase.rpc('revert_subscription_override', {
        p_family_id: familyId,
        p_revert_notes: revertNotes
      });

      if (error) throw error;

      // Force refresh family data cache for this family to immediately reflect changes
      await this.refreshFamilyDataForSubscriptionChange(familyId);
      
      return { success: true, reverted: data };
    } catch (error) {
      console.error('Error reverting subscription override:', error);
      return { success: false, error: error.message };
    }
  }

  // Get active subscription overrides
  async getActiveOverrides() {
    try {
      const { data, error } = await supabase
        .from('active_subscription_overrides')
        .select('*');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching active overrides:', error);
      return [];
    }
  }

  // Get subscription override history
  async getOverrideHistory(familyId = null) {
    try {
      let query = supabase.from('subscription_override_history').select('*');
      
      if (familyId) {
        query = query.eq('family_id', familyId);
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching override history:', error);
      return [];
    }
  }

  // Bulk apply overrides (for promotions)
  async bulkApplyOverrides(familyIds, overrideData) {
    try {
      const results = [];
      for (const familyId of familyIds) {
        const result = await this.applySubscriptionOverride(familyId, overrideData);
        results.push({ familyId, ...result });
      }
      return { success: true, results };
    } catch (error) {
      console.error('Error applying bulk overrides:', error);
      return { success: false, error: error.message };
    }
  }

  // Get families eligible for bulk promotions
  async getEligibleFamiliesForPromotion(criteria = {}) {
    try {
      let query = supabase
        .from('families')
        .select('id, family_name, parent_email, subscription_tier, has_active_override, created_at');

      // Filter by subscription tier
      if (criteria.subscription_tier) {
        query = query.eq('subscription_tier', criteria.subscription_tier);
      }

      // Only families without active overrides
      if (criteria.exclude_active_overrides !== false) {
        query = query.eq('has_active_override', false);
      }

      // Recent signups only
      if (criteria.recent_signups_days) {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - criteria.recent_signups_days);
        query = query.gte('created_at', cutoffDate.toISOString());
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching eligible families:', error);
      return [];
    }
  }

  // Expire overrides manually (admin trigger)
  async expireOverridesNow() {
    try {
      const { data, error } = await supabase.rpc('expire_subscription_overrides');

      if (error) throw error;
      return { success: true, expired_count: data };
    } catch (error) {
      console.error('Error expiring overrides:', error);
      return { success: false, error: error.message };
    }
  }

  // Helper method to refresh family data cache after subscription changes
  async refreshFamilyDataForSubscriptionChange(familyId) {
    try {
      console.log('🔄 Refreshing family data cache after subscription change for family:', familyId);
      
      // Get fresh family data from database
      const { data: freshFamily, error } = await supabase
        .from('families')
        .select(`
          *,
          kids (*),
          shopping_items (*)
        `)
        .eq('id', familyId)
        .single();

      if (error) {
        console.error('Error refreshing family data:', error);
        return;
      }

      // Update localStorage cache if this is the current user's family
      const currentUser = this.getCurrentUser();
      const currentFamily = this.getCachedFamilyData();
      if (currentFamily && currentFamily.id === familyId) {
        const formattedFamily = this.formatFamilyData(freshFamily);
        localStorage.setItem('familyData', JSON.stringify(formattedFamily));
        console.log('✅ Updated family data cache with new subscription tier:', freshFamily.subscription_tier);
        
        // Trigger a custom event that the DataContext can listen to
        window.dispatchEvent(new CustomEvent('familyDataUpdated', { 
          detail: { family: formattedFamily, reason: 'subscription_change' }
        }));
      }
    } catch (error) {
      console.error('Error refreshing family data cache:', error);
    }
  }

  // Get family usage statistics
  async getFamilyUsageStats(familyId) {
    try {
      const { data, error } = await supabase
        .from('usage_tracking')
        .select('*')
        .eq('family_id', familyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching family usage stats:', error);
      return [];
    }
  }

  // Get system-wide usage statistics
  async getSystemUsageStats() {
    try {
      const { data, error } = await supabase
        .from('usage_tracking')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching system usage stats:', error);
      return [];
    }
  }

  // ========================================================
  // ADMIN ACCOUNT MANAGEMENT  
  // ========================================================

  // Create new admin account
  async createAdminAccount(email, password, fullName = 'Admin User') {
    try {
      console.log('🔄 Creating admin account for:', email);

      // Step 1: Create the auth user (email confirmation REQUIRED)
      // Get production URL for email redirects
      const getEmailRedirectUrl = () => {
        if (process.env.REACT_APP_PRODUCTION_URL) {
          return process.env.REACT_APP_PRODUCTION_URL;
        }
        // Use whatever domain the app is actually running on (host-agnostic)
        if (typeof window !== 'undefined' && window.location.origin && !window.location.hostname.includes('localhost')) {
          return window.location.origin;
        }
        return process.env.REACT_APP_REDIRECT_URL || 'http://localhost:3000';
      };

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: getEmailRedirectUrl(),
          data: {
            full_name: fullName,
            role: 'admin'
          }
        }
      });

      if (authError) {
        console.error('Auth creation error:', authError);
        throw new Error(authError.message || 'Failed to create admin user');
      }

      if (!authData.user) {
        throw new Error('User creation failed - no user data returned');
      }

      console.log('✅ Auth user created:', authData.user.id);

      // Step 2: CRITICAL - Check if email confirmation is required (ENFORCE FOR ALL USERS)
      if (!authData.user.email_confirmed_at) {
        console.log('📧 Email confirmation required for admin account');
        return {
          success: true,
          needsConfirmation: true,
          email: email,
          message: 'Admin account created! Please check your email and click the confirmation link to activate your account.',
          userId: authData.user.id
        };
      }

      console.log('✅ Email already confirmed, proceeding with profile creation');

      // Step 3: Only proceed with profile creation if email is confirmed
      let profileData;
      try {
        const { data, error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: authData.user.id,
            email: email,
            full_name: fullName,
            role: 'admin',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single();

        if (profileError) {
          console.error('Profile creation error:', profileError);
          
          // Try to clean up auth user if profile creation fails
          try {
            await supabase.auth.admin.deleteUser(authData.user.id);
          } catch (cleanupError) {
            console.error('Failed to cleanup auth user:', cleanupError);
          }
          
          throw new Error(`Profile creation failed: ${profileError.message}`);
        }
        
        profileData = data;
        console.log('✅ Profile created successfully');
        
      } catch (profileErr) {
        // If profile creation fails due to FK constraint, it might be a timing issue
        if (profileErr.message.includes('foreign key constraint')) {
          throw new Error('Account creation failed. This may be due to email confirmation requirements. Please check your email and try again, or contact an administrator.');
        }
        throw profileErr;
      }

      // Step 5: Add to admin_users table (optional)
      try {
        await supabase
          .from('admin_users')
          .insert({
            email: email,
            full_name: fullName,
            role: 'admin',
            permissions: {
              dashboard: true,
              users: true,
              analytics: true,
              billing: true
            },
            created_at: new Date().toISOString()
          });
        console.log('✅ Admin user record created');
      } catch (adminUserError) {
        console.log('⚠️ Admin users table insert failed (table may not exist):', adminUserError.message);
        // This is not critical - continue
      }

      console.log('✅ Admin account created successfully:', email);
      return {
        success: true,
        user: authData.user,
        profile: profileData,
        message: 'Admin account created successfully!'
      };

    } catch (error) {
      console.error('❌ Error creating admin account:', error);
      
      // Provide user-friendly error messages
      let userMessage = error.message;
      
      if (error.message.includes('User already registered')) {
        userMessage = 'An account with this email already exists. Please use a different email address.';
      } else if (error.message.includes('foreign key constraint')) {
        userMessage = 'Account creation failed due to a database constraint. Please try again or contact support.';
      } else if (error.message.includes('Password')) {
        userMessage = 'Password must be at least 6 characters long.';
      } else if (error.message.includes('Invalid email')) {
        userMessage = 'Please enter a valid email address.';
      }
      
      return { 
        success: false, 
        error: userMessage,
        details: error.message 
      };
    }
  }

  // Complete admin account setup after email confirmation
  async completeAdminAccountSetup(userId, email, fullName = 'Admin User') {
    try {
      console.log('🔄 Completing admin account setup after email confirmation');

      // Step 1: Create profile record
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          email: email,
          full_name: fullName,
          role: 'admin',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (profileError) {
        console.error('Profile creation error:', profileError);
        throw new Error(`Profile creation failed: ${profileError.message}`);
      }

      console.log('✅ Admin profile created');

      // Step 2: Add to admin_users table
      try {
        await supabase
          .from('admin_users')
          .insert({
            email: email,
            full_name: fullName,
            role: 'admin',
            permissions: {
              dashboard: true,
              users: true,
              analytics: true,
              billing: true
            },
            created_at: new Date().toISOString()
          });
        console.log('✅ Admin user record created');
      } catch (adminUserError) {
        console.log('⚠️ Admin users table insert failed (table may not exist)');
        // This is not critical - continue
      }

      return {
        success: true,
        profile: profileData,
        message: 'Admin account setup completed successfully!'
      };

    } catch (error) {
      console.error('❌ Error completing admin account setup:', error);
      return { 
        success: false, 
        error: error.message 
      };
    }
  }

  // Complete family account setup after email confirmation (for regular users)
  async completeFamilyAccountSetup(userId, email, parentName, familyName) {
    try {
      const result = await supabaseHelpers.completeFamilyAccountSetup(userId, email, parentName, familyName);
      return result;
    } catch (error) {
      console.error('❌ Error completing family account setup:', error);
      return { 
        success: false, 
        error: error.message 
      };
    }
  }

  // Get family members for admin dashboard
  async getFamilyMembers(familyId) {
    try {
      // Import FamilyMemberService singleton instance
      const { default: familyMemberService } = await import('./FamilyMemberService');
      
      const members = await familyMemberService.getFamilyMembers(familyId);
      return members;
    } catch (error) {
      console.error('❌ Error fetching family members:', error);
      return [];
    }
  }

  // Create family member for admin dashboard
  async createFamilyMember(familyId, memberData) {
    try {
      // Import FamilyMemberService singleton instance (not class constructor)
      const { default: familyMemberService } = await import('./FamilyMemberService');
      
      const result = await familyMemberService.createFamilyMember(familyId, memberData);
      return result;
    } catch (error) {
      console.error('❌ Error creating family member:', error);
      throw error;
    }
  }

  async cancelFamilyInvitation(memberId) {
    const { default: familyMemberService } = await import('./FamilyMemberService');
    return familyMemberService.cancelInvitation(memberId);
  }

  async removeFamilyMember(memberId) {
    const { default: familyMemberService } = await import('./FamilyMemberService');
    return familyMemberService.removeFamilyMember(memberId);
  }

  async resendFamilyInvitation(memberId) {
    const { default: familyMemberService } = await import('./FamilyMemberService');
    return familyMemberService.resendInvitation(memberId);
  }

  // ========================================================
  // ADMIN USER MANAGEMENT (Pages Function + service role)
  // ========================================================

  async _adminUsersRequest(body) {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      if (!session?.access_token) {
        throw new Error('Admin session required. Sign in again at /admin.');
      }

      const response = await fetch('/api/admin-users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify(body)
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const hint = payload?.details?.hint;
        const base = payload.error || `Request failed (${response.status})`;
        return {
          success: false,
          error: hint ? `${base}. ${hint}` : base,
          details: payload.details || null
        };
      }
      return { success: true, ...payload };
    } catch (error) {
      console.error('Admin users API error:', error);
      return { success: false, error: error.message || 'Admin users API failed' };
    }
  }

  adminListAuthUsers({ page = 1, perPage = 50 } = {}) {
    return this._adminUsersRequest({ action: 'list_auth_users', page, perPage });
  }

  adminLookupEmail(email) {
    return this._adminUsersRequest({ action: 'lookup_email', email });
  }

  adminListMembers({ limit = 100, invitationStatus = null, email = null } = {}) {
    return this._adminUsersRequest({
      action: 'list_members',
      limit,
      invitationStatus,
      email
    });
  }

  adminListAudit({ limit = 50 } = {}) {
    return this._adminUsersRequest({ action: 'list_audit', limit });
  }

  adminCancelInvite(memberId) {
    return this._adminUsersRequest({ action: 'cancel_invite', memberId, confirm: true });
  }

  adminRemoveMember(memberId) {
    return this._adminUsersRequest({ action: 'remove_member', memberId, confirm: true });
  }

  adminDeleteAuthUser(userId, email = null) {
    return this._adminUsersRequest({
      action: 'delete_auth_user',
      userId,
      email,
      confirmPhrase: 'DELETE'
    });
  }

  adminPurgeEmail(email) {
    return this._adminUsersRequest({
      action: 'purge_email',
      email,
      confirmPhrase: 'PURGE'
    });
  }

  adminDeleteFamilyWithAuth(familyId, alsoDeleteOwnerAuth = true) {
    return this._adminUsersRequest({
      action: 'delete_family',
      familyId,
      alsoDeleteOwnerAuth,
      confirmPhrase: 'DELETE'
    });
  }
}

const apiService = new SupabaseApiService();
export default apiService; 